import { Model } from 'dva'
import { queryArticlesByPlatformId, getCacheDur, conversationFeedback, endConversation, getWelcomeMessages, citySwitch, getHotLine, toAiChat } from './service'
import { ChatState } from './interface'
import Moment from 'moment'
import { SENDER_TYPE, MESSAGE_TYPE } from './consts'
import { getStorage } from '@/utils/common'
import { getQueryString } from '@flashex-fe/courier-utils'
import { bdCommonTrack } from '@/utils/bdCourier'
import * as historyStorage from './chatHistoryStorage'

const initialState: ChatState = {
  messages: [],
  freqQueries: [],
  inputValue: '',
  orderNumber: '', // 多个订单需要给大模型携带的参数
  infoId: '', // 多个订单需要给大模型携带的参数
  conversationId: '', // 会话id
  courierName: '',
  moreQuestion: false, // 是否有更多问题
  aiFlag: '', // 是否匹配AI场景
  backClick: false, // 是否点击了后退按钮
  groupId: '',
  question: [], // 本来需要多展示多个问题，大模型返回慢暂且不用了
  hotline: '', // 400电话号码
  showFeedbackModal: false,
  showPhoneButton: false,
  showCustomerService: false, // 七鱼图标的展示
  quickActions: [],
  citySwitch: false,
  inputDisabled: false, // 输入框默认可用
  actionCardPending: false, // 默认无 Action Card 等待
  qiyuFlag: false, // 是否直接跳转七鱼
  equipmentOrderNo: '', // 装备订单号（全局存储，与 orderNumber 不同）
  // 历史记录相关
  sessionId: '',
  courierId: '',
  loadedSessionCount: 0,
  hasMoreHistory: false
}

const model: Model = {
  namespace: 'customerServiceChat',
  state: initialState,

  effects: {
    * init ({ payload }, { call, put, select }) {
      const { longitude, latitude, courierId } = payload
      const cityId = Number(getQueryString('cityId')) || 0

      // ── 1. 历史记录初始化 ──────────────────────────────────────────────────
      // 清理过期数据（过期天数从接口获取，失败则用默认值）
      let cacheDays = historyStorage.DEFAULT_EXPIRY_DAYS
      try {
        const cacheDurRes = yield call(getCacheDur)
        const sessionCacheDays = cacheDurRes?.data?.data?.sessionCacheDays
        if (typeof sessionCacheDays === 'number' && sessionCacheDays > 0) {
          cacheDays = sessionCacheDays
        }
      } catch (e) {
        console.warn('getCacheDur 请求失败，使用默认过期天数', e)
      }
      historyStorage.cleanExpired(courierId, cacheDays)

      // 恢复上次异常退出（进程被杀）时未结束的会话，补全 endTime 使其可被加载
      historyStorage.recoverUnfinishedSessions(courierId)

      // 获取已结束会话数量，判断是否有历史可展示
      const completedCount = historyStorage.getCompletedSessionCount(courierId)

      // 默认加载最近一条历史 session（index=0）
      let loadedSessionCount = 0
      const historyMessages: any[] = []

      if (completedCount > 0) {
        const lastSession = historyStorage.loadSessionByIndex(courierId, 0)
        if (lastSession && lastSession.messages.length > 0) {
          // 将历史消息全部标记为 isOldSession，并绑定该会话的 progressNums
          lastSession.messages.forEach(msg => {
            historyMessages.push({
              ...msg,
              isOldSession: true,
              msgProgressNums: lastSession.progressNums
            })
          })
          // 分隔线放在会话下方，显示结束时间
          historyMessages.push({
            id: `divider_${lastSession.sessionId}`,
            type: MESSAGE_TYPE.SESSION_DIVIDER,
            content: Moment(lastSession.endTime).format('MM月DD日 HH:mm'),
            sender: SENDER_TYPE.AGENT,
            timestamp: lastSession.endTime,
            isOldSession: true
          })
          loadedSessionCount = 1
        }
      }

      // 写入历史消息到 state
      if (historyMessages.length > 0) {
        yield put({ type: 'updateState', payload: { messages: historyMessages } })
      }

      // 创建本次新会话
      const sessionId = historyStorage.createSession(courierId)

      yield put({
        type: 'updateState',
        payload: {
          courierId,
          sessionId,
          loadedSessionCount,
          hasMoreHistory: completedCount > loadedSessionCount
        }
      })

      // ── 2. 先拉取热线信息（获取 groupId，供 qiyuFlag 跳转七鱼使用）──
      try {
        const res = yield call(getHotLine)
        const data = res.data
        if (res && data) {
          yield put({
            type: 'updateState',
            payload: {
              showPhoneButton: data.data.opened,
              hotline: data.data.hotline,
              groupId: data.data.groupId
            }
          })
        }
      } catch (e) {
        console.error('获取热线电话失败：', e)
      }

      // ── 3. 欢迎语 ─────────────────────────────────────────────────────────
      const messages = yield select((state: any) => state.customerServiceChat.messages)
      // 仅当没有本次会话的用户消息时才拉欢迎语
      const hasCurrentUserMsg = messages.some((m: any) => m.sender === SENDER_TYPE.USER && !m.isOldSession)
      if (!hasCurrentUserMsg) {
        try {
          const requestData = yield call(getWelcomeMessages, { cityId, longitude, latitude, courierId })
          const res = requestData.data
          if (res && res.data) {
            // res.data.qiyuFlag = true
            const { welcome, problem, aiFlag, more, problems, nums, qiyuFlag, displayCount, callNums } = res.data
            yield put({
              type: 'updateState',
              payload: { aiFlag, moreQuestion: more, qiyuFlag }
            })

            // qiyuFlag 为 true 时直接跳转七鱼，不再展示欢迎语
            if (qiyuFlag) {
              return
            }

            // 将 nums 持久化到当前会话
            if (typeof nums === 'number' && courierId && sessionId) {
              historyStorage.updateProgressNums(courierId, sessionId, nums)
            }

            if (welcome) {
              yield put({
                type: 'addMessage',
                payload: {
                  id: `welcome_${Date.now()}`,
                  type: MESSAGE_TYPE.TEXT,
                  content: welcome,
                  sender: SENDER_TYPE.AGENT,
                  timestamp: Date.now()
                }
              })
            }

            if (problem && aiFlag) {
              let options = problems && problems.length > 0
                ? problems.map((p: any) => p.children ? p.themeName : p.problem)
                : []
              if (more) {
                options = [...options, '更多问题']
              }
              yield put({
                type: 'addMessage',
                payload: {
                  id: `problem_${Date.now()}`,
                  type: MESSAGE_TYPE.TEXT,
                  content: problem,
                  options: options.length > 0 ? options : undefined,
                  problemsData: problems || [],
                  hasMoreOption: more,
                  msgProgressNums: typeof nums === 'number' ? nums : undefined,
                  displayCount: typeof displayCount === 'number' ? displayCount : undefined,
                  callNums: typeof callNums === 'number' ? callNums : undefined,
                  sender: SENDER_TYPE.AGENT,
                  timestamp: Date.now()
                }
              })
            }

            if (aiFlag) {
              bdCommonTrack('ai_customer_page', {
                event_type: 'view',
                courier_id: Number(courierId),
                aichat_start_time: Moment().format('YYYY-MM-DD HH:mm:ss')
              })
            }
          } else {
            yield put({
              type: 'updateState',
              payload: { aiFlag: false }
            })
          }
        } catch (e) {
          console.error('Failed to load welcome messages', e)
        }
      }

      // ── 3. 其他初始化（quickActions / citySwitch / freqQueries / hotline）──

      yield put({
        type: 'updateState',
        payload: {
          quickActions: [
            { title: '投诉查询', icon: 'complaint', action: () => { } },
            { title: '申诉查询', icon: 'appeal', action: () => { } },
            { title: '里程不准', icon: 'mileage', action: () => { } }
          ]
        }
      })

      try {
        const res = yield call(citySwitch, { cityId: getStorage('cityId') })
        yield put({
          type: 'updateState',
          payload: { citySwitch: res.data.data || false }
        })
      } catch (e) { }

      try {
        const FAQ_PLATFORMID = 401000
        const res = yield call(queryArticlesByPlatformId, { platformId: FAQ_PLATFORMID })
        const data = res.data.data
        if (data && Array.isArray(data)) {
          const allArticles = data.reduce((acc: any[], item: any) => {
            if (item.articles && Array.isArray(item.articles)) {
              return [...acc, ...item.articles]
            }
            return acc
          }, [])
          yield put({ type: 'updateState', payload: { freqQueries: allArticles } })
        }
      } catch (e) { }
    },

    /**
     * 加载更多历史会话（按需分页，每次往前加载一条 session）
     */
    * loadMoreHistory (_, { put, select }) {
      const { courierId, loadedSessionCount, hasMoreHistory } = yield select(
        (state: any) => state.customerServiceChat
      )
      if (!hasMoreHistory || !courierId) return

      const session = historyStorage.loadSessionByIndex(courierId, loadedSessionCount)
      if (!session || session.messages.length === 0) {
        yield put({ type: 'updateState', payload: { hasMoreHistory: false } })
        return
      }

      // 构建要 prepend 的消息（历史消息 + 分隔线在下方）
      const prependMessages: any[] = [
        ...session.messages.map(msg => ({
          ...msg,
          isOldSession: true,
          msgProgressNums: session.progressNums
        })),
        {
          id: `divider_${session.sessionId}`,
          type: MESSAGE_TYPE.SESSION_DIVIDER,
          content: Moment(session.endTime).format('MM月DD日 HH:mm'),
          sender: SENDER_TYPE.AGENT,
          timestamp: session.endTime,
          isOldSession: true
        }
      ]

      const currentMessages = yield select((state: any) => state.customerServiceChat.messages)
      const newLoadedCount = loadedSessionCount + 1
      const completedCount = historyStorage.getCompletedSessionCount(courierId)

      yield put({
        type: 'updateState',
        payload: {
          messages: [...prependMessages, ...currentMessages],
          loadedSessionCount: newLoadedCount,
          hasMoreHistory: completedCount > newLoadedCount
        }
      })
    },

    // 更新一下是否点击了后退按钮
    * updateBackClick ({ payload }, { put }) {
      yield put({
        type: 'updateState',
        payload: { backClick: payload.backClick }
      })
    },

    /**
     * 通用 AI 聊天请求（简化版，只判断请求是否成功）
     */
    * aiChatRequest ({ payload }, { call, put, select }) {
      const { action, params = {}, onSuccess, onError } = payload
      try {
        // 从 state 中获取 orderNumber / equipmentOrderNo
        const { orderNumber, infoId, equipmentOrderNo } = yield select((state: any) => state.customerServiceChat)

        // 获取公共参数 courier_info
        const courierId = getQueryString('courierId') || ''
        const cityId = getQueryString('cityId') || ''
        const courierMobile = getQueryString('user') || ''
        // 优先使用 params 中传递的经纬度，如果没有则使用默认值
        const { latitude = 0, longitude = 0 } = params
        const courierInfo = JSON.stringify({
          courier_id: String(courierId),
          latitude,
          longitude,
          courier_mobile: String(courierMobile),
          courier_city_id: String(cityId)
        })
        const theme_info = JSON.stringify({
          front_tags: 0,
          theme_id: 0,
          eCode: getQueryString('eCode') || ''
        })
        // 构建请求参数
        const requestParams = {
          theme_info,
          action,
          theme_list: JSON.stringify({ theme_id: 0, problem: '', problem_desc: '', cs_mark: '' }),
          order_info: JSON.stringify({
            info_id: infoId,
            order_number: orderNumber,
            equipment_order_number: equipmentOrderNo || ''
          }),
          courier_info: courierInfo,
          ...params
        }

        // 调用 toAiChat 方法
        const response = yield call(toAiChat, requestParams)
        console.log('aiChatRequest response:', response)

        // 处理响应 - 只判断接口是否返回成功
        if (response && response.status === 200 && response.data.data === true) {
          if (onSuccess) {
            onSuccess()
          }
        } else {
          if (onError) {
            onError(new Error(response?.msg || '请求失败'))
          }
        }
      } catch (error) {
        console.error('AI Chat Request Error:', error)
        if (onError) {
          onError(error)
        }
      }
    },

    * submitCondition ({ payload, callback }, { call, put, select }) {
      try {
        const { csMark, conversationId, latitude, longitude, courierId, theme_info } = payload
        const params: any = { latitude, longitude }

        // 添加 csMark（如果存在，兼容旧代码）
        if (csMark !== undefined) {
          params.theme_list = JSON.stringify({ cs_mark: csMark })
        }

        // 添加 theme_info（新的参数格式）
        if (theme_info) {
          params.theme_info = theme_info
        }

        // 添加会话ID（如果存在）
        if (conversationId) {
          params.conversation_id = conversationId
        }

        // 添加闪送员ID（如果存在）
        if (courierId) {
          params.courierId = courierId
        }

        // 使用新的 aiChatRequest 方法
        yield put({
          type: 'aiChatRequest',
          payload: {
            action: 'submitCondition',
            params,
            onSuccess: () => {
              console.log('submitCondition 接口调用成功')
              if (callback) {
                callback({ success: true })
              }
            },
            onError: (error: any) => {
              console.error('Failed to create condition', error)
              if (callback) {
                callback({ success: false, error })
              }
            }
          }
        })
      } catch (e) {
        console.error('Failed to create condition', e)
        // 即使失败也调用回调，让界面可以继续执行
        if (callback) {
          callback({ success: false, error: e })
        }
      }
    },

    * finishConversation ({ payload }, { call, put, select }) {
      const { type } = payload
      // const conversationId = yield select((state: any) => state.customerServiceChat.conversationId)
      // if (conversationId) {
      //   try {
      //      yield call(conversationFeedback, { conversationId, feedbackTag: type })
      //     yield call(endConversation, { conversationId })
      //   } catch (e) { }
      // }
      yield put({ type: 'updateState', payload: { showFeedbackModal: false } })
    },

    * reconnect ({ payload }, { call, put, select }) {
      // 1. 结束当前会话记录
      const { courierId, sessionId, loadedSessionCount } = yield select(
        (state: any) => state.customerServiceChat
      )
      if (courierId && sessionId) {
        historyStorage.endSession(courierId, sessionId)
      }

      // 2. 标记所有现有消息为旧会话
      yield put({ type: 'markOldSession' })

      // 3. 创建新会话
      const newSessionId = courierId ? historyStorage.createSession(courierId) : ''
      const completedCount = courierId ? historyStorage.getCompletedSessionCount(courierId) : 0

      // 4. Reset session state (keep messages) and enable input
      yield put({
        type: 'updateState',
        payload: {
          conversationId: '',
          showFeedbackModal: false,
          feedbackTrigger: undefined,
          inputDisabled: false,
          actionCardPending: false,
          sessionId: newSessionId,
          hasMoreHistory: completedCount > loadedSessionCount
        }
      })

      // 5. 拉取欢迎语（传递经纬度参数）
      const { longitude, latitude, courierId: cid } = payload || {}
      const cityId = Number(getQueryString('cityId')) || 0
      try {
        const requestData = yield call(getWelcomeMessages, { longitude, latitude, courierId: cid, cityId })
        const res = requestData.data
        console.log('reconnect getWelcomeMessages success', res)

        if (res && res.data) {
          const { welcome, problem, more, aiFlag, problems, nums, callNums } = res.data
          yield put({
            type: 'updateState',
            payload: { aiFlag, moreQuestion: more }
          })

          // qiyuFlag 为 true 时直接跳转七鱼，不再展示欢迎语
          // if (qiyuFlag) {
          //   return
          // }

          // 持久化新会话的 nums
          if (typeof nums === 'number' && cid && newSessionId) {
            historyStorage.updateProgressNums(cid, newSessionId, nums)
          }

          if (welcome) {
            yield put({
              type: 'addMessage',
              payload: {
                id: `welcome_${Date.now()}`,
                type: MESSAGE_TYPE.TEXT,
                content: welcome,
                sender: SENDER_TYPE.AGENT,
                timestamp: Date.now()
              }
            })
          }

          if (problem && aiFlag) {
            let options = aiFlag && problems && problems.length > 0
              ? problems.map((p: any) => p.children ? p.themeName : p.problem)
              : []
            if (more) options = [...options, '更多问题']

            yield put({
              type: 'addMessage',
              payload: {
                id: `problem_${Date.now()}`,
                type: MESSAGE_TYPE.TEXT,
                content: problem,
                options: options.length > 0 ? options : undefined,
                problemsData: problems || [],
                hasMoreOption: more,
                msgProgressNums: typeof nums === 'number' ? nums : undefined,
                callNums: typeof callNums === 'number' ? callNums : undefined,
                sender: SENDER_TYPE.AGENT,
                timestamp: Date.now()
              }
            })
          }
        } else {
          yield put({
            type: 'updateState',
            payload: { aiFlag: false }
          })
        }
      } catch (e) {
        yield put({
          type: 'updateState',
          payload: { aiFlag: false }
        })
        console.error('Failed to load welcome messages', e)
      }
    },

    * handleTimeoutWarning (_, { put }) {
      yield put({
        type: 'addMessage',
        payload: {
          id: `sys_warn_${Date.now()}`,
          type: MESSAGE_TYPE.TIMEOUT_WARNING,
          content: '您好，因长时间没有收到您的回复，稍后将自动结束本次会话。',
          sender: SENDER_TYPE.AGENT,
          timestamp: Date.now()
        }
      })
    },

    * handleTimeout (_, { put, select }) {
      // 1. 结束会话记录
      const { courierId, sessionId, loadedSessionCount } = yield select(
        (state: any) => state.customerServiceChat
      )
      if (courierId && sessionId) {
        historyStorage.endSession(courierId, sessionId)
      }

      // 2. 先标记所有现有消息为旧会话
      yield put({ type: 'markOldSession' })

      // 3. Add Timeout Message (这条消息不会被标记为旧会话)
      yield put({
        type: 'addMessage',
        payload: {
          id: `sys_${Date.now()}`,
          type: MESSAGE_TYPE.TIMEOUT_END,
          content: '本次会话超时结束',
          sender: SENDER_TYPE.AGENT,
          actions: [
            { text: '重新连接', actionType: 'reconnect' }
          ],
          timestamp: Date.now()
        }
      })

      // 4. 更新 hasMoreHistory（超时产生了新的已结束会话）
      const completedCount = courierId ? historyStorage.getCompletedSessionCount(courierId) : 0
      const newLoadedCount = loadedSessionCount + 1

      // 5. Show Feedback Modal with specific trigger and disable input
      yield put({
        type: 'updateState',
        payload: {
          showFeedbackModal: true,
          feedbackTrigger: 'timeout',
          inputDisabled: true,
          inputValue: '',
          loadedSessionCount: newLoadedCount,
          hasMoreHistory: completedCount > loadedSessionCount
        }
      })
    }
  },

  reducers: {
    updateState (state, { payload }) {
      const newState = { ...state, ...payload }

      // 当 conversationId 被更新时，同步写入 localStorage
      if (
        payload.conversationId &&
        payload.conversationId !== state.conversationId &&
        state.sessionId &&
        state.courierId
      ) {
        historyStorage.updateConversationId(state.courierId, state.sessionId, payload.conversationId)
      }

      return newState
    },

    addMessage (state, { payload }) {
      const { messages, sessionId, courierId } = state
      const newMessages = [...messages, payload]

      // 持久化：跳过 isStreaming 占位消息、旧会话消息、分隔线
      const shouldSave = !payload.isStreaming &&
        !payload.isOldSession &&
        payload.type !== MESSAGE_TYPE.SESSION_DIVIDER &&
        sessionId &&
        courierId

      if (shouldSave) {
        historyStorage.saveMessage(courierId, sessionId, {
          id: payload.id,
          type: payload.type,
          content: payload.content,
          sender: payload.sender,
          timestamp: payload.timestamp,
          options: payload.options,
          actions: payload.actions,
          orders: payload.orders,
          hasMoreOption: payload.hasMoreOption,
          tag: payload.tag,
          vmiImage: payload.vmiImage,
          equipmentItems: payload.equipmentItems,
          equipmentTitle: payload.equipmentTitle,
          equipmentTicket: payload.equipmentTicket,
          processTag: payload.processTag,
          afterSalesOrderNo: payload.afterSalesOrderNo,
          reasonType: payload.reasonType,
          displayCount: payload.displayCount,
          call_task: payload.call_task,
          call_list: payload.call_list,
          call_info: payload.call_info,
          outboundTaskHistoryTip: payload.outboundTaskHistoryTip,
          callNums: payload.callNums
        })
      } else if (!payload.isStreaming && payload.type !== MESSAGE_TYPE.SESSION_DIVIDER) {
        // 非占位、非分隔线的消息未能保存，打印原因
        console.warn('[addMessage] 消息未持久化:', {
          id: payload.id,
          sender: payload.sender,
          isOldSession: payload.isOldSession,
          sessionId,
          courierId,
          reason: !sessionId ? 'sessionId为空' : !courierId ? 'courierId为空' : 'isOldSession=true'
        })
      }

      return { ...state, messages: newMessages }
    },

    updateMessageContent (state, { payload }) {
      const { id, content, isStreaming } = payload
      const { sessionId, courierId } = state

      const messages = state.messages.map((msg: any) => {
        if (msg.id === id) {
          // 流式完成时（isStreaming: true → false），更新 localStorage 中的消息内容
          if (msg.isStreaming && !isStreaming && sessionId && courierId && !msg.isOldSession) {
            historyStorage.saveMessage(courierId, sessionId, {
              id: msg.id,
              type: msg.type,
              content,
              sender: msg.sender,
              timestamp: msg.timestamp,
              options: msg.options,
              actions: msg.actions,
              tag: msg.tag,
              processTag: msg.processTag,
              afterSalesOrderNo: msg.afterSalesOrderNo,
              reasonType: msg.reasonType,
              displayCount: msg.displayCount
            })
          }
          return { ...msg, content, isStreaming }
        }
        return msg
      })
      return { ...state, messages }
    },

    // 更新外呼任务卡片数据（查看外呼结果后回包更新，而非新增卡片）
    updateOutboundTask (state, { payload }) {
      const { id, call_task, call_list, call_info, callNums, content, outboundTaskHistoryTip } = payload
      const { sessionId, courierId } = state

      const messages = state.messages.map((msg: any) => {
        if (msg.id === id) {
          const updated: any = { ...msg }
          if (call_task) updated.call_task = call_task
          if (call_list) updated.call_list = call_list
          if (call_info) updated.call_info = call_info
          if (callNums !== undefined) updated.callNums = callNums
          if (content !== undefined) updated.content = content
          if (outboundTaskHistoryTip !== undefined) updated.outboundTaskHistoryTip = outboundTaskHistoryTip

          // 同步更新 localStorage
          if (sessionId && courierId && !msg.isOldSession) {
            historyStorage.saveMessage(courierId, sessionId, {
              id: updated.id,
              type: updated.type,
              content: updated.content,
              sender: updated.sender,
              timestamp: updated.timestamp,
              tag: updated.tag,
              call_task: updated.call_task,
              call_list: updated.call_list,
              call_info: updated.call_info,
              callNums: updated.callNums,
              outboundTaskHistoryTip: updated.outboundTaskHistoryTip
            })
          }
          return updated
        }
        return msg
      })
      return { ...state, messages }
    },

    // 根据 callTaskId 批量匹配更新或新建外呼卡片
    updateOrCreateOutboundTasks (state, { payload }) {
      const { items, tag, content, outboundTaskHistoryTip } = payload
      const { sessionId, courierId } = state

      let messages = [...state.messages]
      const toCreate: any[] = []

      items.forEach((item: any) => {
        const { callTaskId, call_task, call_list } = item

        // 查找已有卡片：callTaskId 匹配
        const existingIndex = messages.findIndex((msg: any) => {
          if (tag === 65 && msg.call_task) {
            return msg.call_task[0]?.callTaskId === callTaskId
          }
          if (tag === 64 && msg.call_list) {
            return msg.call_list[0]?.callTaskId === callTaskId
          }
          return false
        })

        if (existingIndex >= 0) {
          // 更新已有卡片
          messages = messages.map((msg: any, idx: number) => {
            if (idx === existingIndex) {
              const updated: any = { ...msg }
              if (call_task) updated.call_task = call_task
              if (call_list) updated.call_list = call_list
              if (content !== undefined) updated.content = content
              if (outboundTaskHistoryTip !== undefined) updated.outboundTaskHistoryTip = outboundTaskHistoryTip

              if (sessionId && courierId && !msg.isOldSession) {
                historyStorage.saveMessage(courierId, sessionId, {
                  id: updated.id,
                  type: updated.type,
                  content: updated.content,
                  sender: updated.sender,
                  timestamp: updated.timestamp,
                  tag: updated.tag,
                  call_task: updated.call_task,
                  call_list: updated.call_list,
                  call_info: updated.call_info,
                  callNums: updated.callNums,
                  outboundTaskHistoryTip: updated.outboundTaskHistoryTip
                })
              }
              return updated
            }
            return msg
          })
        } else {
          // 新建卡片
          const newMsg: any = {
            id: `outbound_task_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            type: MESSAGE_TYPE.OUTBOUND_TASK,
            content: content || '',
            sender: SENDER_TYPE.AGENT,
            tag,
            timestamp: Date.now()
          }
          if (call_task) newMsg.call_task = call_task
          if (call_list) newMsg.call_list = call_list
          if (outboundTaskHistoryTip) newMsg.outboundTaskHistoryTip = outboundTaskHistoryTip

          if (sessionId && courierId) {
            historyStorage.saveMessage(courierId, sessionId, {
              id: newMsg.id,
              type: newMsg.type,
              content: newMsg.content,
              sender: newMsg.sender,
              timestamp: newMsg.timestamp,
              tag: newMsg.tag,
              call_task: newMsg.call_task,
              call_list: newMsg.call_list,
              call_info: newMsg.call_info,
              callNums: newMsg.callNums,
              outboundTaskHistoryTip: newMsg.outboundTaskHistoryTip
            })
          }
          toCreate.push(newMsg)
        }
      })

      messages = [...messages, ...toCreate]
      return { ...state, messages }
    },
    markOldSession (state) {
      // 标记所有现有消息为旧会话
      const messages = state.messages.map((msg: any) => ({
        ...msg,
        isOldSession: true
      }))
      return { ...state, messages }
    },

    removeStreamingMessages (state) {
      // 移除所有正在输入中的消息（isStreaming: true）
      const messages = state.messages.filter((msg: any) => !msg.isStreaming)
      return { ...state, messages }
    },

    clear (state) {
      // 页面卸载时结束当前会话，确保下次进入时能加载到历史记录
      if (state.courierId && state.sessionId) {
        historyStorage.endSession(state.courierId, state.sessionId)
      }
      return { ...initialState }
    }
  }
}

export default model
